import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
  investmentInterest: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  general?: string;
}

interface FormState {
  isSubmitting: boolean;
  isSubmitted: boolean;
  isSuccess: boolean;
  errors: FormErrors;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    investmentInterest: 'general'
  });
  
  const [formState, setFormState] = useState<FormState>({
    isSubmitting: false,
    isSubmitted: false,
    isSuccess: false,
    errors: {}
  });

  // Simple email validation regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  // Phone validation is done by checking digit length (7-15 digits)

  // Client-side validation
  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 50) {
      errors.name = 'Name must be less than 50 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Phone validation (required)
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else {
      const cleanPhone = formData.phone.replace(/\D/g, '');
      if (cleanPhone.length < 7 || cleanPhone.length > 15) {
        errors.phone = 'Please enter a valid phone number (7-15 digits)';
      }
    }

    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    } else if (formData.message.trim().length > 1000) {
      errors.message = 'Message must be less than 1000 characters';
    }

    return errors;
  };

  // Phone formatting function
  const formatPhoneNumber = (value: string): string => {
    const phoneNumber = value.replace(/\D/g, '');
    
    if (!phoneNumber) return '';
    
    const limitedPhone = phoneNumber.substring(0, 15);
    
    if (limitedPhone.length <= 3) {
      return limitedPhone;
    } else if (limitedPhone.length <= 6) {
      return `(${limitedPhone.slice(0, 3)}) ${limitedPhone.slice(3)}`;
    } else if (limitedPhone.length <= 10) {
      return `(${limitedPhone.slice(0, 3)}) ${limitedPhone.slice(3, 6)}-${limitedPhone.slice(6)}`;
    } else {
      return `+${limitedPhone.slice(0, -10)} (${limitedPhone.slice(-10, -7)}) ${limitedPhone.slice(-7, -4)}-${limitedPhone.slice(-4)}`;
    }
  };

  // Real-time validation function
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        if (value.trim().length > 50) return 'Name must be less than 50 characters';
        if (!/^[a-zA-ZçğıöşüÇĞIİÖŞÜ\s\-']+$/.test(value.trim())) {
          return 'Name can only contain letters, spaces, hyphens, and apostrophes';
        }
        return undefined;
      
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        return undefined;
      
      case 'phone':
        if (value && value.length > 0) {
          const cleanPhone = value.replace(/\D/g, '');
          if (cleanPhone.length < 7 || cleanPhone.length > 15) {
            return 'Please enter a valid phone number';
          }
        }
        return undefined;
      
      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 10) return 'Message must be at least 10 characters';
        if (value.trim().length > 1000) return 'Message must be less than 1000 characters';
        return undefined;
      
      default:
        return undefined;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Special handling for phone number - auto-formatting
    if (name === 'phone') {
      const phoneValue = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: phoneValue
      }));
      
      const error = validateField(name, phoneValue);
      setFormState(prev => ({
        ...prev,
        errors: {
          ...prev.errors,
          [name]: error
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      const error = validateField(name, value);
      setFormState(prev => ({
        ...prev,
        errors: {
          ...prev.errors,
          [name]: error
        }
      }));
    }
  };

  // Handle key press for phone input
  const handlePhoneKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const char = String.fromCharCode(e.which);
    if (!/[0-9]/.test(char)) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormState(prev => ({
        ...prev,
        errors
      }));
      return;
    }

    // Set submitting state
    setFormState(prev => ({
      ...prev,
      isSubmitting: true,
      errors: {}
    }));

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://golden-generation-backend.onrender.com';

      // Prepare data for backend - phone should be digits only
      const backendData = {
        ...formData,
        phone: formData.phone.replace(/\D/g, '') // Remove all non-digit characters
      };

      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(backendData),
      });




      const result = await response.json();

      if (response.ok && result.success) {
        setFormState(prev => ({
          ...prev,
          isSubmitting: false,
          isSubmitted: true,
          isSuccess: true
        }));

        // Reset form after success
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          investmentInterest: 'general'
        });

        // Reset success state after 5 seconds
        setTimeout(() => {
          setFormState(prev => ({
            ...prev,
            isSubmitted: false,
            isSuccess: false
          }));
        }, 5000);
      } else {
        // Handle backend validation errors
        setFormState(prev => ({
          ...prev,
          isSubmitting: false,
          errors: { general: result.message || 'Failed to send message. Please try again.' }
        }));
      }
    } catch (error) {
      // Log error in development only
      if (process.env.NODE_ENV === 'development') {
        console.error('Error sending message:', error);
      }
      setFormState(prev => ({
        ...prev,
        isSubmitting: false,
        errors: { general: 'Network error. Please check your connection and try again.' }
      }));
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6 text-golden" />,
      title: "Phone",
      details: "+1 (832) 535-7744",
      description: "Main office line"
    },
    {
      icon: <Mail className="w-6 h-6 text-golden" />,
      title: "Email",
      details: "goldengeneration.ip@gmail.com",
      description: "General inquiries"
    },
    {
      icon: <MapPin className="w-6 h-6 text-golden" />,
      title: "Office",
      details: "11929 W Airport Blvd , Houston, TX, United States, Texas",
      description: "Main office location"
    },
    {
      icon: <Clock className="w-6 h-6 text-golden" />,
      title: "Hours",
      details: "Monday - Saturday: 9:00 AM - 6:00 PM",
      description: "Eastern Time"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-r from-dark-gray to-gray-800 text-white">
        <div className="container-custom text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 fade-in-up">Contact Us</h1>
          <p className="text-xl md:text-2xl text-golden max-w-3xl mx-auto fade-in-up stagger-1">
            Ready to start your investment journey? Let's discuss how we can help you achieve your goals.
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Contact Form */}
            <div className="fade-in-left">
              <h2 className="text-3xl font-bold text-dark-gray mb-6">Send Us a Message</h2>
              
              {formState.isSubmitted && formState.isSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-green-800 mb-2">Thank You!</h3>
                  <p className="text-green-600">
                    Your message has been sent successfully. We'll get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* General Error Message */}
                  {formState.errors.general && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                      <p className="text-red-700">{formState.errors.general}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-golden focus:border-transparent transition-all duration-200 ${
                          formState.errors.name 
                            ? 'border-red-500 animate-pulse' 
                            : 'border-gray-300'
                        }`}
                        placeholder="Enter your full name"
                      />
                      {formState.errors.name && (
                        <p className="mt-1 text-sm text-red-600 flex items-center animate-shake">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {formState.errors.name}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-golden focus:border-transparent transition-all duration-200 ${
                          formState.errors.email 
                            ? 'border-red-500 animate-pulse' 
                            : 'border-gray-300'
                        }`}
                        placeholder="example@domain.com"
                      />
                      {formState.errors.email && (
                        <p className="mt-1 text-sm text-red-600 flex items-center animate-shake">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {formState.errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        onKeyPress={handlePhoneKeyPress}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-golden focus:border-transparent transition-all duration-200 ${
                          formState.errors.phone 
                            ? 'border-red-500 animate-pulse' 
                            : 'border-gray-300'
                        }`}
                        placeholder="+1 (555) 555-5555"
                        maxLength={20}
                      />
                      {formState.errors.phone && (
                        <p className="mt-1 text-sm text-red-600 flex items-center animate-shake">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          {formState.errors.phone}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-transparent transition-colors"
                        placeholder="Enter your company name"
                        maxLength={100}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="investmentInterest" className="block text-sm font-medium text-gray-700 mb-2">
                      Investment Interest
                    </label>
                    <select
                      id="investmentInterest"
                      name="investmentInterest"
                      value={formData.investmentInterest}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-transparent transition-colors"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="investment">Investment Opportunity</option>
                      <option value="partnership">Partnership Discussion</option>
                      <option value="property">Property Information</option>
                      <option value="support">Customer Support</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <div className="relative">
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={5}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-golden focus:border-transparent transition-all duration-200 resize-none ${
                          formState.errors.message 
                            ? 'border-red-500 animate-pulse' 
                            : 'border-gray-300'
                        }`}
                        placeholder="Tell us about your investment goals and how we can help..."
                      />
                      <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                        {formData.message.length}/1000
                      </div>
                    </div>
                    {formState.errors.message && (
                      <p className="mt-1 text-sm text-red-600 flex items-center animate-shake">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {formState.errors.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={formState.isSubmitting}
                    className="w-full btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formState.isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={20} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Information - Aynı kalıyor */}
            <div className="fade-in-right">
              <h2 className="text-3xl font-bold text-dark-gray mb-6">Get in Touch</h2>
              <p className="text-lg text-gray-600 mb-8">
                We're here to help you navigate your investment journey. Reach out to us through 
                any of the channels below, and we'll respond promptly.
              </p>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">{info.icon}</div>
                    <div>
                      <h3 className="font-semibold text-dark-gray">{info.title}</h3>
                      <p className="text-golden font-medium">{info.details}</p>
                      <p className="text-gray-600 text-sm">{info.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Geri kalan sections aynı kalıyor - Maps, Office Hours, CTA */}
      <section className="section-padding bg-light-beige">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-dark-gray mb-4">Visit Our Office</h2>
            <p className="text-lg text-gray-600 mb-8">
            We are ready to welcome you and provide the support you need for your investments.
              </p>
                
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="h-96 w-full">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3467.532258169742!2d-95.59074422446409!3d29.646325537323733!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640e7bdd9105b29%3A0x9d3760494227892!2s11929%20W%20Airport%20Blvd%2C%20Stafford%2C%20TX%2077477%2C%20Amerika%20Birle%C5%9Fik%20Devletleri!5e0!3m2!1str!2str!4v1759071130659!5m2!1str!2str"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Golden Generation Investment Partners Office Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-dark-gray mb-6">Office Hours & Appointments</h2>
              <p className="text-lg text-gray-600 mb-6">
                We value your time and ensure that every meeting is productive and focused on your 
                investment goals. Schedule a consultation to discuss your specific needs.
              </p>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="font-medium text-dark-gray">Monday - Friday</span>
                  <span className="text-gray-600">9:00 AM - 5:00 PM EST</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="font-medium text-dark-gray">Saturday</span>
                  <span className="text-gray-600">9:00 AM - 5:00 PM EST</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="font-medium text-dark-gray">Sunday</span>
                  <span className="text-gray-600">Closed</span>
                </div>
              </div>

              
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop&auto=format&q=80"
                alt="Professional office meeting for real estate consultation"
                className="rounded-lg shadow-2xl"
                loading="lazy"
                width="800"
                height="600"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-golden text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Investment Journey?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Don't wait to begin building your wealth through historic property investment. 
            Contact us today to discuss your goals and discover how we can help you succeed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+1 (832) 535-7744" className="bg-white text-golden hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors">
              Call Us Now
            </a>
            <a href="mailto:info@goldengeneration.com" className="border-2 border-white text-white hover:bg-white hover:text-golden px-8 py-4 rounded-lg font-semibold transition-colors">
              Send Email
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;